import React from 'react'
import { Button, Stack, Text, CloseButton, Group } from '@mantine/core'
import { modals } from '@mantine/modals'

interface AlertProps {
  title?: string
  content: React.ReactNode
  onClose?: () => void
}

const defaultAlertProps = (message: React.ReactNode) => ({
  children: (
    <Stack gap="sm">
      <Text size="md" fw="bold" ta="center">
        Attention needed
      </Text>
      <Text size="sm">{message}</Text>
      <Button onClick={(e) => modals.closeAll()}>OK</Button>
    </Stack>
  ),
  centered: true
})

const Popups = {
  alertFormFailedValidation: () => {
    modals.open({
      ...defaultAlertProps('Please correct the fields outlined in red to proceed.')
    })
  },
  simpleInfoPopUp: ({ title, content, onClose }: AlertProps) => {
    modals.open({
      title: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CloseButton onClick={onClose} />
          {title}
        </div>
      ),
      children: <Text size="sm">{content}</Text>,
      withCloseButton: false
    })
  },

  confirmLogout: (onConfirm?: () => Promise<void> | void) => {
    Popups.customConfirm({
      title: 'Logout?',
      description: 'Are you sure you want to logout?',
      confirmButtonText: 'Yes, logout',
      onConfirm
    })
  },

  confirmDelete: (onConfirm?: () => Promise<void> | void) => {
    Popups.customConfirm({
      title: 'Delete account?',
      description: 'Are you sure you want to delete your account?',
      confirmButtonText: 'Yes, delete',
      confirmColor: 'red',
      onConfirm
    })
  },

  confirmRemoveAllFromWatchlist: (onConfirm?: () => Promise<void> | void) => {
    Popups.customConfirm({
      title: 'Remove all ETFs from Watchlist?',
      description: 'Are you sure you want to remove all ETFs from your Watchlist?',
      confirmButtonText: 'Remove all',
      onConfirm
    })
  },

  confirmRemoveFromWatchlist: (onConfirm?: () => Promise<void> | void) => {
    Popups.customConfirm({
      title: 'Remove ETF from Watchlist?',
      description: 'Are you sure you want to remove this ETF from your Watchlist?',
      confirmButtonText: 'Remove',
      onConfirm
    })
  },

  confirmRemoveFromBookmarks: (onConfirm?: () => Promise<void> | void) => {
    Popups.customConfirm({
      title: 'Remove this bookmark?',
      description: 'Are you sure you want to remove this bookmark?',
      confirmButtonText: 'Remove this bookmark',
      onConfirm
    })
  },

  confirmRemoveAllFromBookmarks: (onConfirm?: () => Promise<void> | void) => {
    Popups.customConfirm({
      title: 'Remove all bookmarks?',
      description: 'Are you sure you want to remove all bookmarks?',
      confirmButtonText: 'Remove all',
      onConfirm
    })
  },
  customConfirm: ({
    title,
    description,
    confirmButtonText,
    cancelButtonText,
    confirmColor,
    onConfirm
  }: {
    title?: React.ReactNode
    description: React.ReactNode
    confirmButtonText?: string
    cancelButtonText?: string
    confirmColor?: string
    onConfirm?: () => void
  }) => {
    return modals.open({
      withCloseButton: false,
      children: (
        <Stack gap={0}>
          {title && (
            <Text ta="center" fz="xl" fw={700}>
              {title}
            </Text>
          )}

          <Text c="grey.7" ta="center" mt="md" mb="xl">
            {description}
          </Text>

          <Group justify="center">
            <Button variant="subtle" onClick={() => modals.closeAll()}>
              {cancelButtonText || 'Cancel'}
            </Button>

            <Button
              variant={confirmColor || undefined}
              size="sm"
              onClick={() => {
                if (onConfirm) onConfirm()
                modals.closeAll()
              }}>
              {confirmButtonText || "Yes, I'm sure"}
            </Button>
          </Group>
        </Stack>
      )
    })
  }
}

export default Popups
